/**
 * A4's resend timer — "আবার পাঠানো যাবে ০:৪২ পরে" — counted down for real.
 *
 * It used to be the drawn string, fixed at ০:৪২, and the resend it promised was not
 * wired. It now counts to the `expires_at` the Worker returned with the code (`POST
 * /v1/auth/otp/request` → `{ outcome: 'sent', expires_at }`), and at zero the sentence
 * becomes a button that asks for a new one.
 *
 * WHY THE CODE'S EXPIRY AND NOT A SHORTER COOLDOWN. The Worker allows five codes an hour
 * for a number and has no resend cooldown of its own, so the only clock it states is how
 * long this code lives. Offering a new code while the old one still works spends one of
 * the five on a code that replaces a live one; waiting it out costs nothing. A person
 * whose code is not arriving has a way that does not wait: the Telegram block at the
 * foot of A4, which asks for the code on the other channel.
 *
 * Split in two so the part that matters is testable without a DOM: the arithmetic and
 * the ticking (`secondsUntil`, `clockBn`, `startCountdown`) are plain functions, and
 * `useCountdown` only holds the number React renders.
 */
import { useEffect, useState } from 'react';
import { toBanglaDigits } from '../../auth/otp.js';

/** Whole seconds from `nowMs` until `expiresAt`, never below zero. Unreadable: zero. */
export function secondsUntil(expiresAt: string | null, nowMs: number): number {
  if (expiresAt === null) return 0;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.ceil((at - nowMs) / 1000));
}

/** `42` → `০:৪২`, `605` → `১০:০৫` — the drawn shape, in Bangla digits. */
export function clockBn(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(s / 60);
  const rest = s % 60;
  return toBanglaDigits(`${minutes}:${rest < 10 ? '0' : ''}${rest}`);
}

/**
 * Call `onTick` now and once a second with the seconds left, until zero. Returns the stop.
 *
 * Each tick reads the clock again rather than counting down from the first reading, so a
 * tab the phone suspended for a minute shows the right time the moment it wakes, instead
 * of resuming from where it froze.
 */
export function startCountdown(
  expiresAt: string | null,
  onTick: (secondsLeft: number) => void,
  now: () => number = Date.now,
): () => void {
  let left = secondsUntil(expiresAt, now());
  onTick(left);
  if (left === 0) return () => {};
  const timer = setInterval(() => {
    left = secondsUntil(expiresAt, now());
    onTick(left);
    if (left === 0) clearInterval(timer);
  }, 1000);
  return () => clearInterval(timer);
}

/** The seconds left on the code, re-rendered once a second while there are any. */
export function useCountdown(expiresAt: string | null): number {
  const [left, setLeft] = useState(() => secondsUntil(expiresAt, Date.now()));
  useEffect(() => startCountdown(expiresAt, setLeft), [expiresAt]);
  return left;
}
